import { useCallback } from 'react';

import { useSendSurveyResponseMutation } from 'core/graphql';

import { buildSurveyAnswers, ISurveyFormValues, ISurveyQuestion } from './transform';

/** Survey submission error codes — plain strings by contract (spec 0011 §4). */
export enum SurveyErrorCode {
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  ALREADY_SUBMITTED = 'ALREADY_SUBMITTED',
  REQUIRED_MISSING = 'REQUIRED_MISSING',
  INVALID_VALUE = 'INVALID_VALUE',
}

export interface ISubmitSurveyResult {
  success: boolean;
  errorCode: string | null;
}

/**
 * Submits a filled survey. Values are JSON.stringify-ed and empty optionals
 * dropped in the transform layer (buildSurveyAnswers) — components pass raw
 * typed Formik values. Refetches the pending list AND `me` after the mutation
 * completes (also on ALREADY_SUBMITTED / NOT_AVAILABLE — the customError means
 * our cached pending state is stale), so the banner, modal and list clear
 * without a reload. Both operation names are active queries at submit time
 * ('me' via the Header, 'StudentPendingSurveys' via the fill page).
 * Network errors propagate as thrown ApolloError — callers handle retry UX.
 */
export const useSubmitSurveyResponse = () => {
  const [mutate, { loading }] = useSendSurveyResponseMutation({
    refetchQueries: ['StudentPendingSurveys', 'me'],
  });

  const submitSurveyResponse = useCallback(
    async (
      assignmentId: string,
      questions: ISurveyQuestion[],
      values: ISurveyFormValues,
    ): Promise<ISubmitSurveyResult> => {
      const { data } = await mutate({
        variables: { assignmentId, answers: buildSurveyAnswers(questions, values) },
      });
      const payload = data?.submitSurveyResponse;
      return {
        success: Boolean(payload?.success),
        errorCode: payload?.customErrors?.[0]?.code ?? null,
      };
    },
    [mutate],
  );

  return { submitSurveyResponse, loading };
};
